import { Alert } from '@mui/material';
import { useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import styles from 'src/pages/accueil/styles/style-setting.module.scss';
import { formatNumber } from 'src/utils/formatNumber';
import ProductsColumns from './products-columns';

type ProductionCycleProductsTableProps = {
  t: (key: string) => string;
  produits: any[];
  showEditAction?: boolean;
  onEditProduct?: (product: any, originalIndex: number) => void;
  pageSize?: number;
  emptyMessageKey?: string;
};

const ProductionCycleProductsTable = ({
  t,
  produits = [],
  showEditAction = false,
  onEditProduct,
  pageSize = 10,
  emptyMessageKey = 'production_cycle.no_products',
}: ProductionCycleProductsTableProps) => {
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(0);

  const withOriginalIndex = useMemo(
    () => produits.map((product: any, originalIndex: number) => ({ product, originalIndex })),
    [produits]
  );

  const filteredProducts = useMemo(() => {
    const needle = search.trim().toUpperCase();
    if (!needle) return withOriginalIndex;

    return withOriginalIndex.filter(({ product }: { product: any }) => {
      const nature = product?.natureProduct?.name || '';
      const description = product?.description || '';
      const unit = product?.unit?.name || '';
      const quantity = product?.quantity != null ? String(product.quantity) : '';
      const currency = product?.currency || 'MAD';
      const amount = product?.amount != null ? String(product.amount) : '0';

      return [nature, description, unit, quantity, currency, amount].some((value) =>
        value.toUpperCase().includes(needle)
      );
    });
  }, [withOriginalIndex, search]);

  const pagesVisited = pageNumber * pageSize;
  const pageCount = Math.ceil(filteredProducts.length / pageSize);

  const rows = filteredProducts.slice(pagesVisited, pagesVisited + pageSize).map(({ product, originalIndex }: any) => {
    const qty = product?.quantity;
    const quantitySort = typeof qty === 'number' ? qty : parseFloat(String(qty ?? '')) || 0;
    const unitName = product?.unit?.name || '';

    return {
      nature: product?.natureProduct?.name || '-',
      description: product?.description || '-',
      quantitySort,
      quantityDisplay: `${qty != null ? formatNumber(qty) : '-'} ${unitName}`.trim(),
      amountValue: Number(product?.amount || 0),
      amount: formatNumber(product?.amount ?? 0),
      currency: product?.currency || 'MAD',
      originalIndex,
      originalProduct: product,
    };
  });

  return (
    <>
      <input
        className="form-control mb-2"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPageNumber(0);
        }}
        placeholder={t('search_unit')}
      />

      {rows.length === 0 ? (
        <Alert variant="outlined" severity="info">
          {t(emptyMessageKey)}
        </Alert>
      ) : (
        <ProductsColumns
          t={t}
          data={rows}
          showEditAction={showEditAction}
          onEditProduct={onEditProduct}
        />
      )}

      {filteredProducts.length > pageSize && (
        <div className="d-flex justify-content-end mt-3">
          <ReactPaginate
            previousLabel="<"
            nextLabel=">"
            pageCount={pageCount}
            onPageChange={({ selected }) => setPageNumber(selected)}
            forcePage={pageNumber}
            containerClassName={styles['paginationButton']}
            previousLinkClassName={styles['previousBtn']}
            nextLinkClassName={styles['nextBtn']}
            disabledClassName={styles['paginationDisabled']}
            activeClassName={styles['paginationActive']}
          />
        </div>
      )}
    </>
  );
};

export default ProductionCycleProductsTable;
